// App do técnico (2026-10-07, rota /tecnico) — sessão PRÓPRIA do técnico
// da Agenda (usuário/senha de Agenda > Técnicos), sem nenhuma relação com a
// sessão de empresa (gpsdrive-client.ts). O token do técnico só vale em
// /api/v1/technician-app/* (segredo próprio no backend), então guardá-lo
// no localStorage não expõe nada do resto do sistema — e o técnico não
// precisa logar de novo a cada abertura do app em campo.
import { GPSDRIVE_API_URL } from "@/lib/api/gpsdrive-client";

const TOKEN_KEY = "gpsdrive.technician.token";
const BASE = `${GPSDRIVE_API_URL}/api/v1/technician-app`;

export type TechAppointmentStatus = "agendado" | "em_andamento" | "finalizado" | "cancelado";
export type TechStep = "en_route" | "arrived" | "start" | "finish";

export interface TechnicianMe {
  id: string;
  name: string;
  phone: string | null;
  username: string | null;
  companyName: string;
  viewAllAppointments?: boolean;
}

export interface TechChecklistItem {
  label: string;
  done: boolean;
  /** Item "só com bloqueio" do checklist do admin (Parâmetro > Checklist). */
  blockOnly?: boolean;
}

export interface TechPhoto {
  id: string;
  // "video" desde 2026-10-13 (thumbUrl null, url = arquivo).
  kind: "photo" | "video";
  url: string;
  thumbUrl: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  createdAt: string;
}

export interface TechAppointment {
  id: string;
  /** false = de outro técnico/sem técnico (permissão "ver todos") — só leitura. */
  mine: boolean;
  technicianName: string;
  title: string;
  clientName: string;
  vehicleName: string | null;
  topicName: string;
  location: string;
  locationType: "loja" | "externa";
  postalCode: string | null;
  street: string | null;
  number: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  notes: string;
  status: TechAppointmentStatus;
  start: string;
  end: string;
  enRouteAt: string | null;
  arrivedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  techNotes: string;
  /** Onde o rastreador ficou no veículo (2026-10-15). */
  installLocation: string;
  closureReasonName: string | null;
  /** Retirada: rastreador devolvido? null = não respondeu. */
  deviceReturned: boolean | null;
  returnedPhotoId: string | null;
  photoCount: number;
  testedDevice: { id: number; name: string | null; imei: string | null; plate: string | null } | null;
  deviceChecks: TechDeviceChecks;
  /** Tag fixa do agendamento (2026-10-13). */
  blockMode: "com_bloqueio" | "sem_bloqueio";
  tagNames: string[];
}

export interface TechAppointmentDetail extends TechAppointment {
  clientPhone: string | null;
  checklist: TechChecklistItem[];
  photos: TechPhoto[];
  /** Tópico de retirada/cancelamento: sem teste, só identificar + devolução. */
  removal: boolean;
  /** Causas pra encerrar (Parâmetro > Causas); com alguma, é obrigatório escolher. */
  closureReasons: { id: string; name: string }[];
  /** Equipamentos que o escritório registrou como levados (atendimento externo). */
  equipment?: { id: string; imei: string; modelo: string; chipNumero: string | null; status: string; role?: "instalar" | "reserva" }[];
}

export class TechnicianSessionExpiredError extends Error {}

// Teste do rastreador dentro do atendimento (2026-10-13): escolhido pelo
// IMEI/placa entre os do raio; o servidor confere tudo e guarda o resultado.
// "external" (2026-10-15): o admin liberou o encerramento sem teste em
// Chamados (teste feito fora do sistema) — dispensa as outras conferências.
export type TechCheckKey = "communication" | "position" | "block" | "unblock" | "external";
export interface TechDeviceCheck {
  status: "pending" | "ok" | "failed";
  at: string;
  detail: string;
  sentAt?: string;
}
export type TechDeviceChecks = Partial<Record<TechCheckKey, TechDeviceCheck>>;

export interface TechDeviceCandidate {
  id: number;
  name: string;
  uniqueId: string;
  plate: string | null;
  online: boolean;
  lastUpdate: string | null;
  /** null na retirada (busca sem raio, rastreador pode estar sem posição). */
  distanceMeters: number | null;
}

export interface TechAppointmentDeviceTest {
  radiusMeters: number;
  allowCommandTest: boolean;
  removal?: boolean;
  deviceReturned?: boolean | null;
  returnedPhotoId?: string | null;
  checks: TechDeviceChecks;
  canFinish: boolean;
  blockers: string[];
  device: {
    id: number;
    name: string;
    uniqueId: string;
    plate: string | null;
    online: boolean;
    lastUpdate: string | null;
    distanceMeters: number | null;
    position: {
      latitude: number;
      longitude: number;
      fixTime: string;
      valid: boolean;
      speedKmh: number;
      satellites: number | null;
      ignition: boolean | null;
      blocked: boolean | null;
      charge: boolean | null;
      powerVolts: number | null;
      batteryLevel: number | null;
    } | null;
  } | null;
}

type Coords = { lat?: number; lng?: number };
function coordsQuery(c: Coords, extra: Record<string, string> = {}): string {
  const q = new URLSearchParams(extra);
  if (c.lat !== undefined) q.set("lat", String(c.lat));
  if (c.lng !== undefined) q.set("lng", String(c.lng));
  const s = q.toString();
  return s ? `?${s}` : "";
}

export function getTechnicianToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // modo privado: sessão vale só enquanto a aba estiver aberta
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getTechnicianToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (res.status === 401 && path !== "/login") {
    setToken(null);
    throw new TechnicianSessionExpiredError("Sessão expirada");
  }
  if (!res.ok) {
    let message = "Não foi possível concluir. Tente de novo.";
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch {
      // corpo vazio
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const technicianAppService = {
  async login(username: string, password: string): Promise<TechnicianMe> {
    const result = await request<{ accessToken: string; technician: TechnicianMe }>("/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    setToken(result.accessToken);
    return result.technician;
  },
  logout() {
    setToken(null);
  },
  me: () => request<TechnicianMe>("/me"),
  listAppointments: (from: Date, to: Date) =>
    request<TechAppointment[]>(
      `/appointments?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
    ),
  getAppointment: (id: string) => request<TechAppointmentDetail>(`/appointments/${id}`),
  searchDevices: (id: string, lat: number | undefined, lng: number | undefined, q: string) =>
    request<{ radiusMeters: number; removal?: boolean; devices: TechDeviceCandidate[] }>(
      `/appointments/${id}/device-test/search${coordsQuery({ lat, lng }, { q })}`,
    ),
  selectDevice: (id: string, deviceId: number, lat: number | undefined, lng: number | undefined) =>
    request<TechAppointmentDeviceTest>(`/appointments/${id}/device-test/select`, {
      method: "POST",
      body: JSON.stringify({ deviceId, lat, lng }),
    }),
  deviceTest: (id: string, coords: Coords) =>
    request<TechAppointmentDeviceTest>(`/appointments/${id}/device-test${coordsQuery(coords)}`),
  deviceCommand: (id: string, type: "engineStop" | "engineResume", lat: number, lng: number) =>
    request<TechAppointmentDeviceTest>(`/appointments/${id}/device-test/command`, {
      method: "POST",
      body: JSON.stringify({ type, lat, lng }),
    }),
  // Retirada (2026-10-15): devolveu o rastreador? + foto dele.
  setDeviceReturned: (id: string, returned: boolean, coords: Coords = {}) =>
    request<TechAppointmentDeviceTest>(`/appointments/${id}/device-return`, {
      method: "POST",
      body: JSON.stringify({ returned, ...coords }),
    }),
  uploadReturnedPhoto: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<TechPhoto>(`/appointments/${id}/device-return/photo`, { method: "POST", body: form });
  },
  advance: (id: string, step: TechStep, coords: Coords = {}, closureReasonId?: string) =>
    request<TechAppointmentDetail>(`/appointments/${id}/step`, {
      method: "POST",
      body: JSON.stringify({ step, ...coords, ...(closureReasonId ? { closureReasonId } : {}) }),
    }),
  saveField: (id: string, data: { techNotes?: string; installLocation?: string; checklist?: TechChecklistItem[] }) =>
    request<TechAppointmentDetail>(`/appointments/${id}/field`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  uploadPhoto: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<TechPhoto>(`/appointments/${id}/photos`, { method: "POST", body: form });
  },
  deletePhoto: (id: string, photoId: string) =>
    request<void>(`/appointments/${id}/photos/${photoId}`, { method: "DELETE" }),
};
